import { jsx } from "react/jsx-runtime";
import { useFloating, useInteractions, useDismiss, useRole, useTransitionStyles, FloatingFocusManager, FloatingPortal, FloatingOverlay } from "@floating-ui/react";
import { styled } from "@linaria/react";
import { useRef, isValidElement, cloneElement, useEffect, useState, useMemo } from "react";
import { useCookbookContext } from "@creditornot/cb-context";
import { createCssVars, assignInlineCssVars } from "@creditornot/cb-utils";
import "./style.css";
const BREAKPOINT_MOBILE_MAX = 549;
function usePrevious(value) {
  const ref = useRef();
  const prev = ref.current;
  ref.current = value;
  return prev;
}
function useClonedNode(node, enable) {
  const clonedNode = useRef();
  const previousNode = usePrevious(node);
  if (!enable) {
    clonedNode.current = void 0;
  }
  if (!clonedNode.current && enable) {
    clonedNode.current = isValidElement(previousNode) ? cloneElement(previousNode) : previousNode;
  }
  return clonedNode.current ?? node;
}
function useConditionalCallback(condition, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const called = useRef(false);
  if (!condition) {
    called.current = false;
  }
  useEffect(() => {
    var _a;
    if (condition && !called.current) {
      (_a = callbackRef.current) == null ? void 0 : _a.call(callbackRef);
      called.current = true;
    }
  }, [condition, callbackRef]);
}
const mediaQuerySupported = typeof window === "object" && typeof window.matchMedia === "function";
const useMediaQuery = (mediaQuery) => {
  var _a;
  const [isMatched, setIsMatched] = useState(
    mediaQuerySupported ? !!((_a = window.matchMedia(mediaQuery)) == null ? void 0 : _a.matches) : null
  );
  useEffect(() => {
    if (!mediaQuerySupported) {
      return;
    }
    const mediaQueryList = window.matchMedia(mediaQuery);
    const handleMediaChange = () => setIsMatched(!!mediaQueryList.matches);
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleMediaChange);
    } else {
      mediaQueryList.addListener(handleMediaChange);
    }
    handleMediaChange();
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", handleMediaChange);
      } else {
        mediaQueryList.removeListener(handleMediaChange);
      }
    };
  }, [mediaQuery]);
  return !!isMatched;
};
const styleParamsCssVars = createCssVars(["borderRadiusParam"]);
const sizeConfigVarNames = ["width", "minWidth", "maxWidth", "height", "minHeight", "maxHeight"];
const cssPxValue = (value) => {
  if (value === "") {
    return void 0;
  }
  return typeof value === "number" ? `${value}px` : value;
};
const getCssRootVariables = (config) => {
  const cssVars = {};
  for (const varName of sizeConfigVarNames) {
    for (const screenSize of ["mobile", "desktop"]) {
      cssVars[`--${screenSize}-${varName}`] = cssPxValue(config[screenSize][varName]);
    }
  }
  return cssVars;
};
const _exp = () => FloatingOverlay;
const Backdrop = /* @__PURE__ */ styled(_exp())({
  name: "Backdrop",
  class: "cb_ModalBase_Backdrop_bbua",
  propsAsIs: true
});
const ScrollContainer = /* @__PURE__ */ styled("div")({
  name: "ScrollContainer",
  class: "cb_ModalBase_ScrollContainer_sdcc",
  propsAsIs: false
});
const Root = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_ModalBase_Root_r1ao",
  propsAsIs: false
});
const getRootTransitionStyles = (direction) => ({
  centered: {
    duration: {
      open: 300,
      close: 200
    },
    initial: {
      opacity: 0,
      transform: "translateY(24px)"
    },
    open: {
      opacity: 1,
      transform: "translateY(0px)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      opacity: 0,
      transform: "translateY(8px)",
      transitionTimingFunction: "easeIn"
    }
  },
  bottomSheet: {
    duration: {
      open: 450,
      close: 350
    },
    initial: {
      transform: "translateY(100%)"
    },
    open: {
      transform: "translateY(0%)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      transform: "translateY(100%)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    }
  },
  sideStartSheet: {
    duration: {
      open: 450,
      close: 350
    },
    initial: {
      transform: direction === "ltr" ? "translateX(-100%)" : "translateX(100%)"
    },
    open: {
      transform: "translateX(0%)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      transform: direction === "ltr" ? "translateX(-100%)" : "translateX(100%)",
      transitionTimingFunction: "easeIn",
      boxShadow: "rgba(0, 0, 0, 0) 0px 0px 24px 0px, rgba(0, 0, 0, 0) 0px 24px 24px 0px"
    }
  },
  sideEndSheet: {
    duration: {
      open: 450,
      close: 350
    },
    initial: {
      transform: direction === "ltr" ? "translateX(100%)" : "translateX(-100%)"
    },
    open: {
      transform: "translateX(0%)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      transform: direction === "ltr" ? "translateX(100%)" : "translateX(-100%)",
      transitionTimingFunction: "easeIn",
      boxShadow: "rgba(0, 0, 0, 0) 0px 0px 24px 0px, rgba(0, 0, 0, 0) 0px 24px 24px 0px"
    }
  },
  fullScreen: {
    duration: {
      open: 300,
      close: 200
    },
    initial: {
      opacity: 0,
      transform: "scale(0.9)"
    },
    open: {
      opacity: 1,
      transform: "scale(1)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      opacity: 0,
      transform: "scale(0.975)",
      transitionTimingFunction: "easeIn"
    }
  }
});
const ModalBase = ({
  animateOnMount = false,
  "aria-describedby": ariaDescribedby,
  "aria-labelledby": ariaLabelledby,
  children: childrenProp,
  className,
  disableDismissOnBackdropClick,
  disableDismissOnEsc,
  disableDismissOnOutsidePress,
  disableModalContentScroll,
  disableScrollLock,
  onDismiss,
  onUnmount,
  open,
  portalElement,
  role,
  sizeConfig = {
    type: "centered"
  },
  style,
  styleParams,
  zIndex
}) => {
  var _a;
  const userPrefersReducedMotion = useMediaQuery("(prefers-reduced-motion)");
  const isOpenChangedSinceFirstRender = useRef(false);
  const previousOpen = usePrevious(open);
  if (previousOpen !== void 0 && previousOpen !== open) {
    isOpenChangedSinceFirstRender.current = true;
  }
  const shouldAnimate = !userPrefersReducedMotion && (!!isOpenChangedSinceFirstRender.current || animateOnMount);
  const {
    context,
    refs
  } = useFloating({
    open,
    onOpenChange: (open2) => {
      if (!open2) {
        onDismiss == null ? void 0 : onDismiss();
      }
    }
  });
  const {
    getFloatingProps
  } = useInteractions([useDismiss(context, {
    escapeKey: !disableDismissOnEsc,
    outsidePress: !disableDismissOnOutsidePress && !disableDismissOnBackdropClick ? (event) => {
      const element = event.target;
      return !(element == null ? void 0 : element.closest('[role="alert"], [role="alertdialog"], [aria-live="polite"]'));
    } : false
  }), useRole(context, {
    role
  })]);
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINT_MOBILE_MAX}px)`);
  const direction = ((_a = useCookbookContext()) == null ? void 0 : _a.direction) ?? "ltr";
  const responsiveSizeConfig = "desktop" in sizeConfig ? sizeConfig : {
    mobile: sizeConfig,
    desktop: sizeConfig
  };
  const {
    styles: backdropStylesAnimated
  } = useTransitionStyles(context, {
    duration: {
      open: 300,
      close: 250
    },
    initial: {
      backgroundColor: "rgba(0, 0, 6, 0)"
    },
    open: {
      backgroundColor: "rgba(0, 0, 6, 0.52)",
      transitionTimingFunction: "cubic-bezier(0.1, 0.8, 0.2, 1.0)"
      // easeOutCirc
    },
    close: {
      backgroundColor: "rgba(0, 0, 6, 0)",
      transitionTimingFunction: "ease"
    }
  });
  const backdropStyles = shouldAnimate ? {
    ...backdropStylesAnimated,
    zIndex
  } : {
    backgroundColor: "rgba(0, 0, 6, 0.52)",
    zIndex
  };
  const rootTransitionStyles = useMemo(() => getRootTransitionStyles(direction), [direction]);
  const transitionType = isMobile ? responsiveSizeConfig.mobile.type : responsiveSizeConfig.desktop.type;
  const transitionStyle = rootTransitionStyles[transitionType];
  const {
    isMounted,
    styles: modalTransitionStyles
  } = useTransitionStyles(context, transitionStyle);
  const rootStyles = shouldAnimate ? modalTransitionStyles : transitionStyle.open;
  const children = useClonedNode(childrenProp, isMounted && !open);
  useConditionalCallback(!isMounted, onUnmount);
  const modal = (shouldAnimate ? isMounted : open) && /* @__PURE__ */ jsx(Backdrop, {
    "data-scroll": !disableModalContentScroll,
    "data-size-desktop-type": responsiveSizeConfig.desktop.type,
    "data-size-mobile-type": responsiveSizeConfig.mobile.type,
    lockScroll: !disableScrollLock,
    style: transitionType === "fullScreen" ? {
      zIndex
    } : backdropStyles,
    children: /* @__PURE__ */ jsx(FloatingFocusManager, {
      context,
      children: /* @__PURE__ */ jsx(Root, {
        "aria-describedby": ariaDescribedby,
        "aria-labelledby": ariaLabelledby,
        "aria-modal": true,
        ...getFloatingProps(),
        className,
        "data-scroll": !disableModalContentScroll,
        "data-size-desktop-type": responsiveSizeConfig.desktop.type,
        "data-size-mobile-type": responsiveSizeConfig.mobile.type,
        ref: refs.setFloating,
        style: {
          ...style,
          ...rootStyles,
          ...getCssRootVariables(responsiveSizeConfig),
          ...assignInlineCssVars(styleParamsCssVars, {
            borderRadiusParam: "16px",
            ...styleParams
          })
        },
        children: /* @__PURE__ */ jsx(ScrollContainer, {
          children
        })
      })
    })
  });
  return portalElement === null ? modal : /* @__PURE__ */ jsx(FloatingPortal, {
    id: "cb-modal-portal",
    children: modal
  });
};
export {
  BREAKPOINT_MOBILE_MAX,
  ModalBase
};
//# sourceMappingURL=index.js.map
